/**
 * App controller — pure static replay mode.
 * Controls ReplayManager directly instead of sending WebSocket messages.
 */

import type { ReplayManager } from "./replay-manager.js";
import type { AppState, StateStore } from "./state.js";
import { toPaneMode, type ServerMessage } from "./types.js";

export type RenderApi = {
  hideComparison: () => void;
  hideRunPicker: () => void;
  renderComparison: (comparison: NonNullable<AppState["comparison"]>) => void;
  renderReplayControls: (state: AppState) => void;
  renderRunPicker: (runs: AppState["availableRuns"], onSelect: (runId: string) => void) => void;
  resetPaneUI: (mode: "baseline" | "treatment") => void;
  setReplayAnimationSpeed: (speed: number) => void;
  showTimeline: (mode: "baseline" | "treatment") => void;
  upsertEventNode: (mode: "baseline" | "treatment", event: Extract<ServerMessage, { type: "event" }>["data"]) => void;
  updatePaneTimer: (mode: Extract<ServerMessage, { type: "progress" }>["mode"], pane: AppState["baseline"], phase: string) => void;
  updateReplayProgress: (state: AppState) => void;
  updateRunButton: (state: AppState) => void;
  showError: (message: string) => void;
};

export function createAppController(params: {
  store: StateStore;
  render: RenderApi;
}) {
  const { store, render } = params;
  let manager: ReplayManager | null = null;

  function setReplayManager(m: ReplayManager) {
    manager = m;
  }

  function handleServerMessage(msg: ServerMessage) {
    switch (msg.type) {
      case "event": {
        const paneMode = toPaneMode(msg.mode);
        render.showTimeline(paneMode);
        store.pushEvent(msg.mode, msg.data);
        render.upsertEventNode(paneMode, msg.data);
        break;
      }

      case "progress":
        store.updatePane(msg.mode, { phase: msg.phase, elapsed: msg.elapsed });
        render.updatePaneTimer(msg.mode, store.get()[toPaneMode(msg.mode)], msg.phase);
        break;

      case "result":
        store.updatePane(msg.mode, { result: msg.data });
        render.updatePaneTimer(msg.mode, store.get()[toPaneMode(msg.mode)], "done");
        if (store.get().treatment.result && store.get().baseline.result) {
          store.update({ phase: "done" });
          render.updateRunButton(store.get());
        }
        break;

      case "comparison":
        store.update({ comparison: msg.data });
        render.renderComparison(msg.data);
        break;

      case "replay-start": {
        store.reset();
        const replaySpeed = store.get().replaySpeed || 4;
        store.update({
          phase: "replaying",
          replayMode: true,
          replayMeta: msg.meta,
          replayTotal: msg.meta.totalDurationMs,
          replayPaused: false,
          replayProgress: 0,
          replayElapsed: 0,
          replayDemoMode: msg.demoMode ?? true,
        });
        render.setReplayAnimationSpeed(replaySpeed);
        render.resetPaneUI("treatment");
        render.resetPaneUI("baseline");
        render.hideComparison();
        render.updateRunButton(store.get());
        render.renderReplayControls(store.get());
        break;
      }

      case "replay-progress":
        store.update({
          replayProgress: msg.position,
          replayElapsed: msg.elapsed,
          replayTotal: msg.total,
          replaySpeed: msg.speed,
          replayPaused: msg.paused,
          replayDemoMode: msg.demoMode ?? store.get().replayDemoMode,
        });
        render.setReplayAnimationSpeed(msg.speed);
        render.updateReplayProgress(store.get());
        break;

      case "replay-seek":
        store.update({ phase: "replaying", comparison: null });
        store.resetPane("treatment");
        store.resetPane("baseline");
        render.resetPaneUI("treatment");
        render.resetPaneUI("baseline");
        render.hideComparison();
        render.updateRunButton(store.get());
        break;

      case "replay-end":
        store.update({
          phase: "done",
          replayPaused: false,
          replayProgress: 1,
        });
        render.setReplayAnimationSpeed(0);
        render.updateReplayProgress(store.get());
        render.updateRunButton(store.get());
        break;

      case "error":
        render.showError(msg.message);
        break;
    }
  }

  function handleReplayButton() {
    const state = store.get();
    if (state.showRunPicker) {
      store.update({ showRunPicker: false });
      render.hideRunPicker();
      return;
    }

    store.update({ showRunPicker: true });
    render.renderRunPicker(state.availableRuns, (runId) => {
      store.update({ showRunPicker: false });
      render.hideRunPicker();
      manager?.startReplay(runId, 4).then((error) => {
        if (error) render.showError(error);
      });
    });
  }

  function handleStopClick() {
    manager?.stop();
    store.update({
      phase: "idle",
      replayMode: false,
      replayMeta: null,
    });
    render.setReplayAnimationSpeed(0);
    render.updateRunButton(store.get());
    render.renderReplayControls(store.get());
  }

  function handleDocumentClick(target: HTMLElement) {
    if (target.closest("[data-replay-pause]")) {
      manager?.pause();
      store.update({ replayPaused: true });
      render.updateReplayProgress(store.get());
    }
    if (target.closest("[data-replay-resume]")) {
      manager?.resume();
      store.update({ replayPaused: false });
      render.updateReplayProgress(store.get());
    }
    if (target.closest("[data-replay-speed]")) {
      const btn = target.closest("[data-replay-speed]") as HTMLElement;
      const speed = Number(btn.dataset.replaySpeed);
      if (speed > 0) {
        manager?.setSpeed(speed);
        store.update({ replaySpeed: speed });
        render.setReplayAnimationSpeed(speed);
        render.renderReplayControls(store.get());
      }
    }
    if (target.closest("[data-replay-demo-toggle]")) {
      const btn = target.closest("[data-replay-demo-toggle]") as HTMLElement;
      const enabled = btn.dataset.replayDemoToggle === "true";
      manager?.setDemoMode(enabled);
      store.update({ replayDemoMode: enabled });
      render.renderReplayControls(store.get());
    }
    if (target.closest("[data-stop-replay]")) {
      handleStopClick();
    }
  }

  function handleReplaySeek(target: HTMLElement, clientX: number) {
    const bar = target.closest(".replay-progress-track") as HTMLElement | null;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    const position = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    manager?.seek(position);
  }

  return {
    setReplayManager,
    handleDocumentClick,
    handleReplayButton,
    handleReplaySeek,
    handleServerMessage,
  };
}
